import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CircularProgress } from "@nextui-org/react";

// Segments of the call-volume gauge. `filter` drives the /calls redirection;
// `not_analyzed` has no filter and is therefore not clickable.
const SEGMENTS = [
  { key: "green", label: "Green Calls", color: "bg-grade-high", filter: "green" },
  { key: "orange", label: "Orange Calls", color: "bg-grade-medium", filter: "orange" },
  { key: "red", label: "Red Calls", color: "bg-grade-low", filter: "red" },
  { key: "not_analyzed", label: "Not analyzed", color: "bg-gray-300", filter: null },
];

const CallsGaugeBar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { calls, loading_calls } = useSelector((state) => state.analytics.analytics);

  const total = calls?.total_calls || 0;
  const green = calls?.green_calls || 0;
  const orange = calls?.orange_calls || 0;
  const red = calls?.red_calls || 0;
  const counts = {
    green,
    orange,
    red,
    not_analyzed: Math.max(0, total - green - orange - red),
  };

  const goToFilter = (filter) => {
    if (!filter) return;
    navigate(`/calls?filter=${filter}`);
  };

  const pct = (value) => (total > 0 ? (value / total) * 100 : 0);

  return (
    <div className="bg-white rounded-md px-[32px] py-[24px] shadow-custom-light w-full">
      <div className="flex items-end justify-between gap-6 flex-wrap mb-5">
        <button
          type="button"
          onClick={() => navigate("/calls")}
          className="text-left group"
        >
          <div className="text-[14px] text-gray-400">{t("Number of Calls")}</div>
          {loading_calls ? (
            <CircularProgress
              aria-label="Loading..."
              color="primary"
              size="sm"
            />
          ) : total > 0 ? (
            <div className="text-4xl font-semibold text-primary group-hover:opacity-80 transition-opacity">
              {total.toLocaleString("fr-FR")}
            </div>
          ) : (
            <div className="text-4xl font-semibold text-gray-300">{t("N/A")}</div>
          )}
        </button>

        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {total > 0 &&
            SEGMENTS.map((seg) => (
              <button
                type="button"
                key={seg.key}
                onClick={() => goToFilter(seg.filter)}
                disabled={!seg.filter}
                className={`flex items-center gap-2 ${
                  seg.filter ? "cursor-pointer hover:opacity-70" : "cursor-default"
                }`}
              >
                <span className={`w-3 h-3 rounded-full ${seg.color}`} />
                <span className="text-sm text-gray-600">{t(seg.label)}</span>
                <span className="text-sm font-semibold text-[#171717]">
                  {counts[seg.key].toLocaleString("fr-FR")}
                </span>
                <span className="text-xs text-gray-400">{Math.round(pct(counts[seg.key]))}%</span>
              </button>
            ))}
        </div>
      </div>

      <div className="relative flex w-full h-5 rounded-full overflow-hidden bg-gray-100">
        {total > 0 ? (
          SEGMENTS.map((seg) => {
            const width = pct(counts[seg.key]);
            if (width <= 0) return null;
            return (
              <button
                type="button"
                key={seg.key}
                onClick={() => goToFilter(seg.filter)}
                disabled={!seg.filter}
                title={`${t(seg.label)}: ${counts[seg.key].toLocaleString("fr-FR")}`}
                style={{ width: `${width}%` }}
                className={`${seg.color} h-full transition-all ${
                  seg.filter ? "cursor-pointer hover:opacity-80" : "cursor-default"
                }`}
              />
            );
          })
        ) : (
          <span className="absolute inset-0 flex items-center justify-center text-xs text-gray-400">
            {!loading_calls && t("No data")}
          </span>
        )}
      </div>
    </div>
  );
};

export default CallsGaugeBar;
